import React, { useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, LinearProgress, TextField, Typography } from "@mui/material";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useTriggerVerifyPendingOrgMutation } from "state/rtkQuery/sync/syncQueries";
import sortByLabel from "utils/sortByLabel";

import { normalizeApiError } from "./syncFormat";
import { Panel, Pill } from "./SyncParts";
import { useSyncTasks } from "./SyncTasksContext";

const Stat = ({ value, label, color }) => (
  <Box sx={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", p: "10px 12px" }}>
    <Typography sx={{ fontSize: 20, fontWeight: 700, color }}>{value ?? "—"}</Typography>
    <Typography sx={{ color: "#64748b", fontSize: 11.5 }}>{label}</Typography>
  </Box>
);

// Checks one organization's pending joins and removals. Both outcomes of the trigger are successes: 202 "accepted" and
// 200 "already_running" (which just resumes watching the run that is already going).
const VerifyOrgPanel = () => {
  const { data: orgs } = useGetActiveOrganizationsQuery();
  const options = useMemo(() => sortByLabel(orgs, "name"), [orgs]);
  const { tasks, trackTask } = useSyncTasks();
  const [trigger, { isLoading }] = useTriggerVerifyPendingOrgMutation();
  const [org, setOrg] = useState(null);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const latest = org ? tasks.find((task) => task.kind === "verify-org" && task.orgId === org._id) : null;

  const run = async () => {
    setError(null);
    setInfo(null);
    try {
      const body = await trigger({ orgId: org._id }).unwrap();
      const already = body.status === "already_running";
      if (already) setInfo(body.message || "A verification is already running for this organization. Showing its progress.");
      trackTask({
        id: body.task_id,
        kind: "verify-org",
        orgId: org._id,
        title: `Verify pending: ${org.name}`,
        taskName: "sync.user.verify_pending (organization)",
        queue: "sync.user",
        scope: `Organization · ${org.name}`,
        // An already-running answer does not mean this person started that run.
        owner: already ? undefined : body.owner,
        triggeredBy: already ? undefined : body.triggered_by,
        backendScope: already ? undefined : body.scope,
        state: already ? "STARTED" : "PENDING",
      });
    } catch (err) {
      setError(normalizeApiError(err));
    }
  };

  const stats = latest?.result?.stats;
  const running = latest && (latest.state === "PENDING" || latest.state === "STARTED");

  return (
    <Panel title="Verify one organization" subtitle="check pending joins and removals for a single organization" sx={{ mt: 2 }}>
      <Box sx={{ p: "16px 20px" }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Autocomplete
            size="small"
            options={options}
            value={org}
            onChange={(_, value) => {
              setOrg(value);
              setInfo(null);
              setError(null);
            }}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            noOptionsText="No matching organization"
            sx={{ width: 340 }}
            renderInput={(params) => <TextField {...params} label="Organization" placeholder="Select an organization…" />}
          />
          <Button variant="contained" onClick={run} disabled={!org || isLoading || Boolean(running)} sx={{ background: "#86BC25", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { background: "#6a9a1e", boxShadow: "none" } }}>
            {isLoading ? "Queuing…" : "Verify pending"}
          </Button>
          <Typography sx={{ color: "#64748b", fontSize: 12.5, maxWidth: 420, pt: 1 }}>
            Moves REQUESTED users who are already in GitHub to Joined, and REMOVE_REQUESTED users who are gone to Removed.
          </Typography>
        </Box>

        {error && <Alert severity={error.uncertain ? "warning" : "error"} sx={{ mt: 2 }}>{error.message}</Alert>}
        {info && <Alert severity="info" sx={{ mt: 2 }}>{info}</Alert>}

        {latest && (
          <Box sx={{ mt: 2 }} data-testid="verify-result">
            {running && (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                  <Pill tone="blue">{latest.state === "PENDING" ? "Submitted" : "Running"}</Pill>
                  <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>
                    {latest.state === "PENDING" ? "Waiting for a worker…" : latest.note || "Orchestrator is running…"}
                  </Typography>
                </Box>
                <LinearProgress sx={{ height: 6, borderRadius: 6 }} />
              </>
            )}
            {latest.state === "SUCCESS" && (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Pill tone={latest.result?.success === false ? "warn" : "ok"}>{latest.result?.success === false ? "Finished with problems" : "Complete"}</Pill>
                  <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>{latest.result?.message}</Typography>
                </Box>
                {stats && (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.25, mt: 1.5 }}>
                    <Stat value={stats.users_checked} label="users checked" />
                    <Stat value={stats.users_joined} label="joined" color="#4d7a10" />
                    <Stat value={stats.users_removed} label="removed" color="#b91c1c" />
                    <Stat value={stats.users_updated} label="updated" />
                  </Box>
                )}
                {stats && (
                  <Typography sx={{ color: "#64748b", fontSize: 12, mt: 1 }}>
                    {stats.pairs_successful}/{stats.pairs_processed} status buckets succeeded. Buckets are the two pending statuses (requested, remove-requested); the other counts are users.
                    {stats.pairs_failed > 0 && ` ${stats.pairs_failed} failed.`}
                  </Typography>
                )}
              </>
            )}
            {latest.state === "FAILURE" && <Alert severity="error">{latest.result?.error || latest.error || "The verification failed."}</Alert>}
            {latest.lookupFailed && <Alert severity="warning" sx={{ mt: 1 }}>Couldn't read the latest status: {latest.lookupMessage}.</Alert>}
          </Box>
        )}
      </Box>
    </Panel>
  );
};

export default VerifyOrgPanel;
